import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import type { AccountGroup } from '../../lib/types';

interface FlatNode {
  group: AccountGroup;
  level: number;
  hasChildren: boolean;
}

function flatten(nodes: AccountGroup[], expanded: Set<string>, level = 0, out: FlatNode[] = []): FlatNode[] {
  for (const g of nodes) {
    const hasChildren = Boolean(g.children && g.children.length > 0);
    out.push({ group: g, level, hasChildren });
    if (hasChildren && expanded.has(g.id)) {
      flatten(g.children!, expanded, level + 1, out);
    }
  }
  return out;
}

function allIds(nodes: AccountGroup[], out: string[] = []): string[] {
  for (const g of nodes) {
    if (g.children && g.children.length > 0) {
      out.push(g.id);
      allIds(g.children, out);
    }
  }
  return out;
}

interface GroupTreeProps {
  tree: AccountGroup[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}

/** Keyboard-navigable account-group tree: ↑↓ move, →/← expand/collapse or step in/out, Enter selects. */
export function GroupTree({ tree, selectedId, onSelect }: GroupTreeProps) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(allIds(tree)));
  const [focusIndex, setFocusIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const flat = useMemo(() => flatten(tree, expanded), [tree, expanded]);

  useEffect(() => {
    if (focusIndex >= flat.length) setFocusIndex(Math.max(0, flat.length - 1));
  }, [flat.length, focusIndex]);

  function toggle(id: string, open?: boolean) {
    setExpanded((prev) => {
      const next = new Set(prev);
      const shouldOpen = open ?? !next.has(id);
      if (shouldOpen) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (flat.length === 0) return;
    const current = flat[focusIndex];
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusIndex((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      if (!current) return;
      if (current.hasChildren && !expanded.has(current.group.id)) {
        toggle(current.group.id, true);
      } else if (current.hasChildren) {
        setFocusIndex((i) => Math.min(i + 1, flat.length - 1));
      }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      if (!current) return;
      if (current.hasChildren && expanded.has(current.group.id)) {
        toggle(current.group.id, false);
      } else if (current.level > 0) {
        const parentIndex = [...flat.keys()].reverse().find((i) => (flat[i]?.level ?? 0) < current.level && i < focusIndex);
        if (parentIndex !== undefined) setFocusIndex(parentIndex);
      }
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (current) onSelect(current.group.id);
    }
  }

  return (
    <div
      ref={containerRef}
      role="tree"
      aria-label="Account groups"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      className="flex-1 overflow-y-auto p-1 text-[12.5px] outline-none focus-visible:ring-1 focus-visible:ring-[var(--focus-ring)]"
    >
      <div
        role="treeitem"
        aria-selected={selectedId === null}
        onClick={() => onSelect(null)}
        className={clsx('cursor-pointer rounded-[2px] px-1.5 py-0.5', selectedId === null ? 'bg-[var(--bg-selected)] font-medium' : 'hover:bg-[var(--bg-hover)]')}
      >
        All ledgers
      </div>
      {flat.map((node, i) => (
        <div
          key={node.group.id}
          role="treeitem"
          aria-selected={selectedId === node.group.id}
          aria-expanded={node.hasChildren ? expanded.has(node.group.id) : undefined}
          onClick={() => {
            setFocusIndex(i);
            onSelect(node.group.id);
          }}
          style={{ paddingLeft: `${6 + node.level * 14}px` }}
          className={clsx(
            'flex cursor-pointer items-center gap-1 rounded-[2px] py-0.5 pr-1.5',
            i === focusIndex && 'outline outline-1 outline-[var(--border-strong)]',
            selectedId === node.group.id ? 'bg-[var(--bg-selected)] font-medium' : 'hover:bg-[var(--bg-hover)]',
          )}
        >
          <span
            className="w-3 shrink-0 text-center text-[var(--text-faint)]"
            onClick={(e) => {
              e.stopPropagation();
              if (node.hasChildren) toggle(node.group.id);
            }}
          >
            {node.hasChildren ? (expanded.has(node.group.id) ? '▾' : '▸') : ''}
          </span>
          <span className="truncate">{node.group.name}</span>
          {node.group.isPrimary && <span className="ml-auto shrink-0 text-[10px] text-[var(--text-faint)]">primary</span>}
        </div>
      ))}
    </div>
  );
}
